/**
 * Cat stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const catStickersPack: ToilePack = {
    slug: 'cat-stickers',
    category: 'sticker',
    name: 'Cat stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'loaf', label: 'Cat loaf', subject: 'a cat sitting as a loaf with paws tucked, eyes half closed'},
        {key: 'stretch', label: 'Big stretch', subject: 'a cat mid-stretch with its back arched and tail up'},
        {key: 'box', label: 'Cat in a box', subject: 'a cat squeezed into a small cardboard box, ears poking out'},
    ],
    styles: [
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a black cat instead', 'for a laptop lid', 'as a sleepy version'],
    directionHint: 'try: a black cat instead',
    seo: {
        title: 'Cat Stickers: Loaf, Stretch, Box',
        metaDescription: 'Nine cat stickers — a cat loaf, a big stretch, a cat in a box — in kawaii, bold cartoon, and hand-drawn styles.',
        h1: 'Cat stickers: loaf, stretch, box',
        tagline: 'Nine stickers, three cat poses, three styles each.',
        intro: "The internet's three cat poses, drawn as die-cut stickers: a cat sitting as a loaf with paws tucked, a mid-stretch arch, and a cat squeezed into a cardboard box. Each one in kawaii, bold cartoon, and loose hand-drawn finishes — the same joke works without a caption. Ask for a black cat, a laptop-lid composition, or a sleepy version, and the same three styles carry it.",
        useCases: [
            'a laptop or water bottle',
            'a planner or bullet journal page',
            'a phone case sticker sheet',
            'a gift for a cat person',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine cat stickers — a cat loaf, a cat mid-stretch, and a cat in a box — in kawaii, bold cartoon, and hand-drawn finishes.'},
            {q: 'Can I get a specific cat, like a black cat or a different pose?', a: 'Yes — the direction field takes any variation, including a black cat instead, a laptop-lid composition, or a sleepier version of any pose.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales from a small vinyl sticker up to a full A4 sticker sheet without redrawing.'},
            {q: 'Are these based on a real cat or a meme?', a: 'No — the poses are common cat behaviour, not a specific meme or photograph; every sticker is an original illustration.'},
        ],
    },
}
